import { BlockError } from '@/blocks.04-project/block-error/block-error';
import forEach from 'lodash-es/forEach';
import debounce from 'lodash-es/debounce';
import toArray from 'lodash-es/toArray';
import throttle from 'lodash-es/throttle';

/***
 * Класс боковой панели в корзине страницы
 * @example
 * Методы:
 * setResultPrice(price) - Устанавливает "Итоговую стоимость" внутри cart-panel__result-price
 * Переменные:
 * --cart-panel-height - высота панели
 * Классы:
 * .cart-panel_fixed - класс, который ставит панель статично
 */
class CartPanel {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с классом cart-panel
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'CartPanel', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.CartPanel = this;
    this.init();
  }

  init = () => {
    this.priceFixed = this.block.querySelector('.cart-panel__fixed');
    if (!this.priceFixed) return this.BlockError.error('Отсутствует .cart-panel__fixed');

    this.headerElem = document.querySelector('.header');
    if (!this.headerElem) return this.BlockError.error('Отсутствует .header');

    this.Header = this.headerElem.Header;
    if (!this.Header) return this.BlockError.error('Отсутствует Header у .header');

    this.resize();
    window.addEventListener('resize', this.resize, { passive: true });

    this.block.classList.add('cart-panel_inited');
  };

  resize = debounce(() => {
		window.removeEventListener('scroll', this.throttleScroll);
    if (window.innerWidth < 1200) {
      document.body.style.setProperty('--cart-panel-offset', this.priceFixed.offsetHeight + 'px');
      window.addEventListener('scroll', this.throttleScroll, {passive: true});
			if (!this.observer || this.headerOffsetBottom !== this.Header.getOffsetBottom()) {
				this.initObserver();
			}
    } else {
      document.body.style.setProperty('--cart-panel-offset', '0px');
      this.block.style.setProperty('--cart-panel-height', '0px');
      this.block.classList.remove('cart-panel_fixed');
      this.observer = this.observer?.disconnect();
    }
  }, 320);

	throttleScroll = throttle(() => {
    const isScrollTop = window.scrollY < this.lastScroll;
		this.block.classList.toggle('cart-panel_hide', isScrollTop);
    document.body.style.setProperty('--cart-panel-offset', isScrollTop ? '0px' : this.priceFixed.offsetHeight + 'px');
		this.lastScroll = window.scrollY;
	}, 300);

  initObserver = () => {
    this.headerOffsetBottom = this.Header.getOffsetBottom();
    this.offsetBottom = this.headerOffsetBottom - 10;

    this.block.style.setProperty('--cart-panel-height', '0px');
    this.block.style.setProperty('--cart-panel-height', this.priceFixed.offsetHeight + 'px');

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(this.observerCallback, {
      rootMargin: `-${window.screen.height - this.offsetBottom}px 0px -${Math.abs(this.offsetBottom)}px 0px`,
      threshold: 0,
    });

    this.observer.observe(this.block);
  };

  observerCallback = (entries) => {
    forEach(entries, () => {
      let scrollTop = window.scrollY + window.screen.height - this.offsetBottom;
      this.block.classList.toggle('cart-panel_fixed', scrollTop <= this.getBottomCoordinates(this.block));
    });
  };

  getBottomCoordinates = (block) => block.getBoundingClientRect().bottom + window.scrollY;

  /***
   * Устанавливает "Итоговую стоимость" внутри cart-panel__result-price
   * @param price {String|Number}
   */
  setResultPrice = (price) => {
    forEach(toArray(this.block.querySelectorAll('.cart-panel__result-price')), (item) => {
      item.textContent = price;
    });
  };

  destroy = () => {
    window.removeEventListener('resize', this.resize, { passive: true });
    if (this.observer) this.observer.disconnect();
    if (this.block) {
      this.block.classList.remove('cart-panel_inited');
      this.block.classList.remove('cart-panel_fixed');
      delete this.block.CartPanel;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('.cart-panel:not(.cart-panel_inited)')).forEach((elem) => new CartPanel(elem));
  }
}

document.addEventListener('DOMContentLoaded', CartPanel.init);

// Экспорт;
window.Block.CartPanel = CartPanel;
